import React from "react";

import { useNotification } from "@refinedev/core";
import { ArrowDown, ArrowUp } from "lucide-react";

import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAreaRepository } from "@/pages/areas/hooks/use-area-repository";
import type { AreaListItem } from "@/types/area";

const byOrder = (a: AreaListItem, b: AreaListItem): number =>
  (a.partOrder ?? Number.MAX_SAFE_INTEGER) -
    (b.partOrder ?? Number.MAX_SAFE_INTEGER) ||
  b.venueCount - a.venueCount ||
  a.name.localeCompare(b.name);

/** The parts whose position (index) differs from their stored `partOrder`. */
export const movedParts = (
  ordered: readonly AreaListItem[],
): { id: string; partOrder: number }[] =>
  ordered
    .map((p, i) => ({ id: p.id, partOrder: i, was: p.partOrder }))
    .filter((p) => p.was !== p.partOrder)
    .map(({ id, partOrder }) => ({ id, partOrder }));

interface ReorderPartsDialogProps {
  parts: readonly AreaListItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

export const ReorderPartsDialog = ({
  parts,
  open,
  onOpenChange,
  onSaved,
}: ReorderPartsDialogProps) => {
  const repo = useAreaRepository();
  const { open: notify } = useNotification();
  const [items, setItems] = React.useState<AreaListItem[]>([]);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) setItems([...parts].sort(byOrder));
  }, [open, parts]);

  const move = (index: number, delta: number) =>
    setItems((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      for (const { id, partOrder } of movedParts(items)) {
        await repo.update(id, { partOrder });
      }
      notify?.({ type: "success", message: "Part order saved" });
      onOpenChange(false);
    } catch (error) {
      notify?.({
        type: "error",
        message: "Could not save the part order",
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setSaving(false);
      // A reorder that stopped half way still moved some parts: reload either way.
      onSaved?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reorder parts</DialogTitle>
          <DialogDescription>
            Drag a part, or use the arrows. Apps pick up changes within 1 hour.
          </DialogDescription>
        </DialogHeader>
        <SortableList
          items={items}
          orientation="vertical"
          handle
          onChanged={setItems}
          render={(item: AreaListItem, idx?: number) => (
            <div className="flex w-full items-center justify-between gap-2">
              <span className="truncate">{item.name}</span>
              <div className="flex">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label={`Move ${item.name} up`}
                  disabled={idx === 0}
                  onClick={() => move(idx ?? 0, -1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label={`Move ${item.name} down`}
                  disabled={idx === items.length - 1}
                  onClick={() => move(idx ?? 0, 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
